import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RefAccountsSecuritiesRequest } from '../modules/models/admin-model';
import { AdminService } from '../modules/services/admin.service';
import { ExcelExportService } from '../modules/shared/excel-export.service';
import { UserService } from '../modules/shared/user.service';

const APPLICATION_NAME = 'Supervision';

// Fields the user must fill in before saving a row (uniqueIdentifier is auto-generated; createdBy/lastUpdatedBy are system-set).
const REQUIRED_FIELDS: (keyof RefAccountsSecuritiesRequest)[] = [
  'referenceCode', 'referenceValue', 'referenceDesc', 'include', 'exclude', 'isActive', 'application'
];

const EXPORT_COLUMNS: { key: keyof RefAccountsSecuritiesRequest; label: string }[] = [
  { key: 'referenceCode', label: 'Ref Code' },
  { key: 'referenceValue', label: 'Ref Value' },
  { key: 'referenceDesc', label: 'Description' },
  { key: 'productCode', label: 'Product Code' },
  { key: 'assetTypeCode', label: 'Asset Type' },
  { key: 'assetSubTypeCode', label: 'Asset Sub-Type' },
  { key: 'include', label: 'Include' },
  { key: 'exclude', label: 'Exclude' },
  { key: 'isActive', label: 'Active' },
  { key: 'inactiveDate', label: 'Inactive Date' },
  { key: 'application', label: 'Application' },
  { key: 'alertCode', label: 'Alert Code' },
  { key: 'createdBy', label: 'Created By' },
  { key: 'lastUpdatedBy', label: 'Last Updated By' }
];

@Component({
  selector: 'app-admin',
  standalone: false,
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class AdminComponent implements OnInit {
  displayedColumns = [
    'referenceCode', 'referenceValue', 'referenceDesc', 'productCode',
    'assetTypeCode', 'assetSubTypeCode', 'include', 'exclude', 'isActive',
    'inactiveDate', 'application', 'alertCode', 'createdBy', 'lastUpdatedBy', 'actions'
  ];

  dataSource = new MatTableDataSource<RefAccountsSecuritiesRequest>([]);

  referenceCodes: string[] = [];
  applications: string[] = [];

  editingRow: RefAccountsSecuritiesRequest | null = null;
  private originalRow: RefAccountsSecuritiesRequest | null = null;
  isNewRow = false;
  isSaving = false;

  constructor(
    private adminService: AdminService,
    private excelExportService: ExcelExportService,
    private userService: UserService,
    private snackBar: MatSnackBar,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  goBack(): void {
    this.router.navigate(['/home']);
  }

  exportToExcel(): void {
    if (!this.dataSource.data.length) {
      return;
    }

    const headers = EXPORT_COLUMNS.map(column => column.label);
    const rows = this.dataSource.data.map(item =>
      EXPORT_COLUMNS.map(column => item[column.key] ?? '')
    );
    this.excelExportService.generateExcel(headers, rows, undefined, undefined, 'ReviewList', 'AdminReviewList', 'AdminReviewList');
  }

  loadData(): void {
    this.adminService.search(APPLICATION_NAME).subscribe({
      next: items => {
        this.dataSource.data = items;
        this.referenceCodes = this.getDistinctValues(items, 'referenceCode');
        this.applications = this.getDistinctValues(items, 'application');
      },
      error: () => this.snackBar.open('Failed to load Admin data.', 'Close', { duration: 3000 })
    });
  }

  private getDistinctValues(items: RefAccountsSecuritiesRequest[], field: keyof RefAccountsSecuritiesRequest): string[] {
    const values = new Set<string>();
    for (const item of items || []) {
      const val = item[field];
      if (val != null && String(val).trim() !== '') {
        values.add(String(val).trim());
      }
    }
    return Array.from(values).sort();
  }

  private buildEmptyRow(): RefAccountsSecuritiesRequest {
    return {
      Flag: '',
      uniqueIdentifier: '',
      referenceCode: '',
      referenceValue: '',
      referenceDesc: '',
      productCode: null,
      assetTypeCode: null,
      assetSubTypeCode: null,
      include: 'N',
      exclude: 'N',
      isActive: 'Y',
      inactiveDate: null,
      application: APPLICATION_NAME.toUpperCase(),
      alertCode: 'NA',
      createdBy: null,
      lastUpdatedBy: null
    };
  }

  startAddNew(): void {
    if (this.editingRow) {
      return;
    }
    const newRow = this.buildEmptyRow();
    this.dataSource.data = [newRow, ...this.dataSource.data];
    this.isNewRow = true;
    this.originalRow = null;
    this.editingRow = newRow;
  }

  isEditing(row: RefAccountsSecuritiesRequest): boolean {
    return this.editingRow === row;
  }

  onActiveChange(row: RefAccountsSecuritiesRequest): void {
    row.inactiveDate = row.isActive === 'N' ? this.formatToday() : null;
  }

  isAssetDisabled(row: RefAccountsSecuritiesRequest): boolean {
    return row.referenceCode === 'ACCOUNT' || row.referenceCode === 'PARTY';
  }

  onReferenceCodeChange(row: RefAccountsSecuritiesRequest): void {
    if (this.isAssetDisabled(row)) {
      row.assetTypeCode = null;
      row.assetSubTypeCode = null;
    }
  }

  private formatToday(): string {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
  }

  startEdit(row: RefAccountsSecuritiesRequest): void {
    if (this.editingRow) {
      return;
    }
    this.isNewRow = false;
    this.originalRow = { ...row };
    this.editingRow = row;
  }

  cancelEdit(): void {
    if (this.isNewRow && this.editingRow) {
      this.dataSource.data = this.dataSource.data.filter(r => r !== this.editingRow);
    } else if (this.editingRow && this.originalRow) {
      Object.assign(this.editingRow, this.originalRow);
    }
    this.editingRow = null;
    this.originalRow = null;
    this.isNewRow = false;
  }

  private validate(row: RefAccountsSecuritiesRequest): boolean {
    const missing = REQUIRED_FIELDS.some(field => {
      const value = row[field];
      return value === null || value === undefined || String(value).trim() === '';
    });
    if (missing) {
      this.snackBar.open('Please fill in all required fields.', 'Close', { duration: 3000 });
      return false;
    }
    return true;
  }

  private generateUniqueIdentifier(row: RefAccountsSecuritiesRequest): string {
    return `${row.application}_${row.referenceCode}_${row.alertCode}_${row.referenceValue}`.toUpperCase();
  }

  saveRow(row: RefAccountsSecuritiesRequest): void {
    if (!this.validate(row) || this.isSaving) {
      return;
    }

    row.uniqueIdentifier = this.generateUniqueIdentifier(row);
    if (row.isActive === 'N' && !row.inactiveDate) {
      row.inactiveDate = this.formatToday();
    }
    const currentUserId = this.userService.getUser()?.userid || '';
    this.isSaving = true;

    if (this.isNewRow) {
      const payload: RefAccountsSecuritiesRequest = { ...row, createdBy: currentUserId, lastUpdatedBy: currentUserId };
      this.adminService.addItem(payload).subscribe({
        next: () => {
          this.isSaving = false;
          this.editingRow = null;
          this.originalRow = null;
          this.isNewRow = false;
          this.loadData();
        },
        error: () => {
          this.isSaving = false;
          this.snackBar.open('Failed to add row.', 'Close', { duration: 3000 });
        }
      });
      return;
    }

    const payload: RefAccountsSecuritiesRequest = { ...row, lastUpdatedBy: currentUserId };
    this.adminService.updateItem(payload).subscribe({
      next: () => {
        this.isSaving = false;
        this.editingRow = null;
        this.originalRow = null;
        this.loadData();
      },
      error: () => {
        this.isSaving = false;
        this.snackBar.open('Failed to update row.', 'Close', { duration: 3000 });
      }
    });
  }
}
